import type { FieldValue, Timestamp } from 'firebase/firestore';
import type { BaseFirestoreEntity } from '../../../types/commonTypes';

export type JourneyCardVariant = 'yellow' | 'blue' | 'green' | 'pink';

export type JourneyItemType = 'chat' | 'check-in' | 'story' | 'lesson' | 'community-question' | 'tool';

export interface JourneyItemRewardStats {
    type: JourneyItemType;
    count: number;
    minBrainPoints: number;
    maxBrainPoints: number;
    minEnergyCost: number;
    maxEnergyCost: number;
}

export type JourneyProgressType = 'fixed' | 'undetermined';

export interface JourneyItemFieldToCollect {
    order: number;
    key: string;
    description: string;
    aiGuidance: string;
}

// ---------------------------------------------------------------------------
// Item Template types
// ---------------------------------------------------------------------------

export interface JourneyItemTemplateBaseFirestore extends BaseFirestoreEntity {
    id?: string;
    type: JourneyItemType;
    order: number;
    progressType: JourneyProgressType;
    maxProgress?: number;
    title: string;
    description: string;
    ollieText: string;
    textTemplatingPrompt: string;
    brainPoints: number;
    energyCost: number;
}

export interface JourneyItemTemplateChatFirestore extends JourneyItemTemplateBaseFirestore {
    type: 'chat';
    aiPromptGuidance: string;
    aiOpeningStatementGuidance: string;
    aiClosingStatementGuidance: string;
    objective: string;
    fieldsToCollect: JourneyItemFieldToCollect[];
}

export interface JourneyItemTemplateCheckInFirestore extends JourneyItemTemplateBaseFirestore {
    type: 'check-in';
    aiPromptGuidance: string;
}

export interface JourneyItemTemplateStoryFirestore extends JourneyItemTemplateBaseFirestore {
    type: 'story';
    imageUrl: string;
    successStoryId: string;
}

export interface JourneyItemTemplateLessonFirestore extends JourneyItemTemplateBaseFirestore {
    type: 'lesson';
    imageUrl: string;
    lessonColor: string;
    sectionId: string;
    lessonId: string;
}

export interface JourneyItemTemplateCommunityQuestionFirestore extends JourneyItemTemplateBaseFirestore {
    type: 'community-question';
    questionId: string;
}

export interface JourneyItemTemplateToolFirestore extends JourneyItemTemplateBaseFirestore {
    type: 'tool';
    imageUrl: string;
    toolName: string;
    toolId: string;
}

export type JourneyItemTemplateFirestore =
    | JourneyItemTemplateChatFirestore
    | JourneyItemTemplateCheckInFirestore
    | JourneyItemTemplateStoryFirestore
    | JourneyItemTemplateLessonFirestore
    | JourneyItemTemplateCommunityQuestionFirestore
    | JourneyItemTemplateToolFirestore;

// ---------------------------------------------------------------------------
// Section & Chapter Template types
// ---------------------------------------------------------------------------

export interface JourneySectionTemplateFirestore extends BaseFirestoreEntity {
    id?: string;
    header: string;
    ollieText: string;
    color: JourneyCardVariant;
    order: number;
}

export interface JourneyChapterTemplateFirestore extends BaseFirestoreEntity {
    id?: string;
    title: string;
    number: number;
    description: string;
    ollieText: string;
    imageUrl: string;
    color: string;
}

export interface JourneySectionOption {
    chapterId: string;
    chapterNumber: number;
    chapterTitle: string;
    sectionId: string;
    sectionHeader: string;
    sectionOrder: number;
}

// ---------------------------------------------------------------------------
// User journey status enums
// ---------------------------------------------------------------------------

export const JourneyItemStatus = {
    LOCKED: 'locked',
    AVAILABLE: 'available',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    CLAIMED: 'claimed',
} as const;
export type JourneyItemStatus = (typeof JourneyItemStatus)[keyof typeof JourneyItemStatus];

export const JourneySectionStatus = {
    LOCKED: 'locked',
    COMPLETED: 'completed',
    CLAIMED: 'claimed',
} as const;
export type JourneySectionStatus = (typeof JourneySectionStatus)[keyof typeof JourneySectionStatus];

export const JourneyChapterStatus = {
    LOCKED: 'locked',
    AVAILABLE: 'available',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
} as const;
export type JourneyChapterStatus = (typeof JourneyChapterStatus)[keyof typeof JourneyChapterStatus];

export interface JourneyState {
    energy: number;
    usableBrainPoints: number;
    currentChapterNumber: number;
    dayNumber: number;
    dailyGainedBrainPoints: number;
    dailyMaxBrainPoints: number;
    meterFilledAt?: FieldValue | Timestamp;
    createdAt?: FieldValue | Timestamp;
    updatedAt?: FieldValue | Timestamp;
}

// ---------------------------------------------------------------------------
// User journey compiled progress types (user_info/{uid}/journey_chapters/...)
// ---------------------------------------------------------------------------

export interface JourneyItemProgressFirestore extends BaseFirestoreEntity {
    id: string;
    templateId: string;
    type: JourneyItemType;
    order: number;
    progressType: JourneyProgressType;
    progress: number;
    maxProgress?: number;
    status: JourneyItemStatus;
    title: string;
    description: string;
    ollieText: string;
    brainPoints: number;
    energyCost: number;
    /** Set by the chat reset action; the app wipes the device-local transcript when it changes. */
    chatResetAt?: number;
    // Optional type-specific fields (not edited here, kept for display/round-trip)
    fieldsToCollect?: Record<string, boolean>;
    aiPromptGuidance?: string;
    imageUrl?: string;
    successStoryId?: string;
    lessonColor?: string;
    sectionId?: string;
    lessonId?: string;
    questionId?: string;
    questionResponseCount?: number;
    toolName?: string;
    toolId?: string;
}

export interface JourneySectionProgressFirestore extends BaseFirestoreEntity {
    id: string;
    templateId: string;
    header: string;
    ollieText: string;
    color: JourneyCardVariant;
    order: number;
    status: JourneySectionStatus;
    items: JourneyItemProgressFirestore[];
}

export interface JourneyChapterProgressFirestore extends BaseFirestoreEntity {
    id: string;
    templateId: string;
    title: string;
    number: number;
    description: string;
    ollieText: string;
    imageUrl: string;
    color: string;
    status: JourneyChapterStatus;
    sections: JourneySectionProgressFirestore[];
}

export type UserJourneyTree = JourneyChapterProgressFirestore[];

// ---------------------------------------------------------------------------
// Journey leaderboard entry
// ---------------------------------------------------------------------------

export interface JourneyLeaderboardEntry {
    uid: string;
    email: string | null;
    displayName: string | null;
    currentChapterNumber: number;
    dayNumber: number;
    usableBrainPoints: number;
    energy: number;
    updatedAt: Date | null;
}

